import { PlusIcon } from "@xola/icons";
import { ComboBox, FormGroup, Input, Label, SubmitButton } from "@xola/ui-kit";
import { getDemographicCatalog } from "common";
import { produce } from "immer";
import { sortBy } from "lodash";
import React, { useState } from "react";
import { Except } from "type-fest";
import {
    CatalogItem,
    Experience,
    Guest,
    GuestNameSource,
    OPEN_UNLIMITED,
    Package,
    Timeslot,
    TimeslotUnit,
} from "types";
import { getPackageDemographicCatalog } from "../../../models/Package";
import { SecondaryButton } from "../../Common/SecondaryButton";

export interface GuestPayload extends Partial<Except<Guest, "unit" | "checkInCount">> {
    name: string;
    unit: { id: ID };
    source?: GuestNameSource;
    _remove?: boolean;
}

interface AddGuestFormProps {
    experience: Experience;
    guests: GuestPayload[];
    timeslot: Timeslot;
    max: number;
    pkg?: Package;
    onChange: (guests: GuestPayload[]) => void;
}

export const AddGuestForm = ({ experience, guests, timeslot, max, pkg, onChange }: AddGuestFormProps) => {
    const [showAddForm, setShowAddForm] = useState(false);

    const handleAddGuest = async (name: string, catalog: CatalogItem) => {
        setShowAddForm(false);
        const unit = getTimeslotUnit(catalog, timeslot.units);
        if (unit) {
            const newGuests = produce(guests, (guestsDraft: GuestPayload[]) => {
                guestsDraft.push({ name, unit: { id: unit.id } });
            });
            onChange(newGuests);
        }
    };

    if (max <= 0) {
        // User has hit the limit to add more guests, this form should not be shown
        return <ZeroCapacity />;
    }

    const catalog = pkg ? getPackageDemographicCatalog(pkg) : getDemographicCatalog(experience);

    if (showAddForm) {
        return <AddGuest catalog={catalog} onAdd={handleAddGuest} onCancel={() => setShowAddForm(false)} />;
    }

    // In case that we have opened capacity we want to show option for adding guests
    return (
        <div>
            {max ? (
                <div className="flex items-center space-x-4">
                    <SecondaryButton size="small" onClick={() => setShowAddForm(true)}>
                        <PlusIcon className="mr-1" />
                        Add Guest
                    </SecondaryButton>
                    <div data-testid="demographic-list-availabilty-count" className="text-sm font-bold">
                        available {timeslot.capacity.open === OPEN_UNLIMITED ? "99+" : max}
                    </div>
                </div>
            ) : (
                <ZeroCapacity />
            )}
        </div>
    );
};

interface AddGuestProps {
    onAdd: (name: string, catalog: CatalogItem) => void;
    catalog: CatalogItem[];
    onCancel: () => void;
}

const AddGuest = ({ catalog, onAdd, onCancel }: AddGuestProps) => {
    const [name, setName] = useState<string>("");
    const options = sortBy(catalog, "sequence").map((item) => ({ value: item.id, label: item.name }));
    const defaultValue = options.length === 1 ? options[0] : undefined;
    const [value, setValue] = useState<{ value?: string; label: string } | undefined>(defaultValue);

    const handleChange = (option: { value: string; label: string } | null) => {
        if (option) {
            setValue(option);
        }
    };

    const handleAdd = () => {
        if (value && name) {
            onAdd(name, catalog.find((item) => item.id === value.value)!);
        }
    };

    return (
        <div>
            <div className="flex space-x-2">
                <div className="flex-1">
                    <FormGroup>
                        <Label>Name</Label>
                        <Input value={name} onChange={(e: any) => setName(e.target.value)} />
                    </FormGroup>
                </div>

                <div className="flex-1">
                    <FormGroup>
                        <Label>Demographic</Label>
                        <ComboBox isClearable={false} options={options} value={value} onChange={handleChange} />
                    </FormGroup>
                </div>
            </div>
            <div className="flex justify-end space-x-2">
                <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                <SubmitButton disabled={!value || !name} onClick={handleAdd}>
                    Add
                </SubmitButton>
            </div>
        </div>
    );
};

export const getTimeslotUnit = (catalogItem: CatalogItem, units: TimeslotUnit[]) => {
    const timeslotUnit = units.find((unit) => unit.template.id === catalogItem.template?.id);
    // Secondary demographics aren't included in timeslot.units, but their unit is always
    // available on the catalog item itself, so fall back to that.
    return timeslotUnit ?? (catalogItem.unit ? { id: catalogItem.unit.id } : undefined);
};

const ZeroCapacity = () => <div className="pt-1">0 Capacity Available</div>;
